import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

const TURNSTILE_SCRIPT_PREFIX = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

type TurnstileApi = {
  render: (container: HTMLElement | string, options: Record<string, unknown>) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export type TranzitTurnstileWidgetHandle = {
  reset: () => void;
};

type Props = {
  siteKey: string;
  onToken: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
  disabled?: boolean;
  theme?: 'light' | 'dark' | 'auto';
  frameClassName?: string;
};

/**
 * Cloudflare Turnstile for Tranzit public registration (B4).
 * Parent should call `reset()` after each POST that consumed the token.
 */
const TranzitTurnstileWidget = forwardRef<TranzitTurnstileWidgetHandle, Props>(function TranzitTurnstileWidget(
  { siteKey, onToken, onExpire, onError, disabled, theme = 'auto', frameClassName },
  ref
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);
  const onExpireRef = useRef(onExpire);
  const onErrorRef = useRef(onError);
  onTokenRef.current = onToken;
  onExpireRef.current = onExpire;
  onErrorRef.current = onError;

  const tearDown = () => {
    if (widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.remove(widgetIdRef.current);
      } catch {
        /* ignore */
      }
    }
    widgetIdRef.current = null;
  };

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
    },
  }));

  useEffect(() => {
    if (!siteKey || disabled) {
      tearDown();
      return;
    }
    const el = containerRef.current;
    if (!el) return;

    const mount = () => {
      if (!containerRef.current || !window.turnstile) return;
      tearDown();
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token: string) => {
          onTokenRef.current(token);
        },
        'expired-callback': () => {
          onExpireRef.current?.();
        },
        'error-callback': () => {
          onErrorRef.current?.();
        },
        theme,
        appearance: 'always',
      });
    };

    if (window.turnstile) {
      mount();
      return () => tearDown();
    }

    let script = document.querySelector(`script[src^="${TURNSTILE_SCRIPT_PREFIX}"]`) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.src = `${TURNSTILE_SCRIPT_PREFIX}?render=explicit`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    const onLoad = () => {
      mount();
    };
    script.addEventListener('load', onLoad);

    return () => {
      script?.removeEventListener('load', onLoad);
      tearDown();
    };
  }, [siteKey, disabled, theme]);

  if (!siteKey || disabled) {
    return null;
  }

  return (
    <div className={frameClassName ? `tranzit-turnstile ${frameClassName}` : 'tranzit-turnstile'}>
      <div ref={containerRef} className="tranzit-turnstile-host" />
    </div>
  );
});

export default TranzitTurnstileWidget;
